import { useApolloClient, useMutation, useQuery } from '@apollo/client/react'
import {
  AssociationOrdering,
  AttemptSummary,
  Button,
  ConfirmationDialog,
  ErrorState,
  FailureNotice,
  FieldMessage,
  LiveRegion,
  LoadingState,
  MatchingBoard,
  Notice,
  ProgressLinear,
  QuestionOption,
  QuestionOptionGroup,
  RichText,
  ScoreIndicator,
  Section,
  TextInput,
  buttonClasses,
  cn,
  handleOptionLetterKey,
  useLanguage,
  usePageTitle,
  useToast,
  useTranslate,
  useViewportInset,
} from '@athena/ui'
import { Link, useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react'

import { invalidateStudentActivity } from './cache.js'
import { startBlockedKey } from '../learn/startBlocked.js'
import { AttemptTimer } from './AttemptTimer.js'
import { CompletionReview } from './CompletionReview.js'
import { useFirstUserErrorText } from '../../lib/user-errors.js'

import {
  fromSavedAnswers,
  toSubmittedAnswers,
  unansweredPositions,
  type AnswerMap,
  type LocalAnswer,
} from './answers.js'
import { useAttemptAutosave } from './useAttemptAutosave.js'
import {
  AttemptDetailDocument,
  MyAssignedPracticesDocument,
  MyAttemptsDocument,
  StartAttemptDocument,
  SubmitAttemptDocument,
  type AttemptDetailQuery,
} from '../../graphql/generated/operations.js'

type Attempt = NonNullable<AttemptDetailQuery['attempt']>
type Question = Attempt['questions'][number]

interface Score {
  correctCount: number
  questionCount: number
  percent: number
}

/**
 * The attempt runner (handoff, Organisms > Student practice).
 *
 * No sidebar and no header: a practice in progress holds the question and
 * nothing else, and leaving takes a deliberate action. One question per view,
 * forward and back within the attempt, and a single primary action.
 *
 * Nothing here knows which answer is right. `AttemptQuestion` has no field for
 * it, the repository behind it never loads the answer key, and the score
 * arrives from the server after submit (V1.md sections 5 and 10). The
 * correctness data this screen has is a number out of a number.
 */
export interface AttemptRunnerProps {
  attemptId: string
}

export function AttemptRunner({ attemptId }: AttemptRunnerProps) {
  const t = useTranslate()
  // What the sticky header and footer cover, so focus is scrolled clear of them.
  const topInset = useViewportInset('top')
  const bottomInset = useViewportInset('bottom')
  const userErrorText = useFirstUserErrorText()
  const language = useLanguage()
  const navigate = useNavigate()
  const client = useApolloClient()
  const toast = useToast()

  const { data, loading, error, refetch } = useQuery(AttemptDetailDocument, {
    variables: { id: attemptId },
    /**
     * The server's answer replaces the cached one on every read, rather than
     * being merged into it. `secondsRemaining` is the reason: it is computed
     * per request, so a cache that kept the first one would hand a returning
     * tab a number from ten minutes ago.
     */
    fetchPolicy: 'cache-and-network',
    // Reconcile without a full reload. A student who comes back to a suspended
    // tab, or whose connection dropped and returned, gets the server's number
    // again rather than a local estimate that has been drifting.
    notifyOnNetworkStatusChange: true,
  })
  const { data: history } = useQuery(MyAttemptsDocument)
  const [submitAttempt] = useMutation(SubmitAttemptDocument, {
    update(cache, { data }) {
      if (data?.submitAttempt.attempt) invalidateStudentActivity(cache)
    },
    /**
     * The results screen numbers this attempt and lists the ones before it, so
     * it needs a history that includes the attempt just submitted.
     *
     * It would not otherwise have one. `myAttempts` is already in the cache
     * from the home screen, and an attempt started since then was never added
     * to that list — a mutation result normalises the entity but cannot know
     * to append it to a field's list. Submitting updates the entity's status
     * in place, which does nothing for an attempt the list never held, so a
     * student finishing their first go was counted among zero attempts.
     *
     * Awaited rather than left in flight, so the score appears with its real
     * number instead of a wrong one that corrects itself a moment later. The
     * submit button is already showing "Checking…" for the wait.
     */
    refetchQueries: [MyAttemptsDocument],
    awaitRefetchQueries: true,
  })
  const [startAttempt, { loading: starting }] = useMutation(StartAttemptDocument, {
    update(cache, { data }) {
      if (data?.startAttempt.attempt) invalidateStudentActivity(cache)
    },
  })
  const [answers, setAnswers] = useState<AnswerMap>({})
  const [index, setIndex] = useState(0)
  /**
   * The same two values, readable the moment they change.
   *
   * A save reads what to send when it is sent, which can be in the same event
   * that changed them — "Next" moves on and saves in one click — and state
   * set in that event is not visible until the next render.
   */
  const answersRef = useRef<AnswerMap>({})
  const indexRef = useRef(0)
  const [saveError, setSaveError] = useState<string>()
  // Seeded once per attempt. Without the guard, the cache update that follows
  // every save would re-seed from the server and undo anything typed since.
  const seeded = useRef<string | undefined>(undefined)
  const [ready, setReady] = useState(false)
  const [exiting, setExiting] = useState(false)
  const [closing, setClosing] = useState(false)
  /** Submitting waits for the answers to be saved first, and says "Checking…" for both. */
  const [submitPhase, setSubmitPhase] = useState<'idle' | 'saving' | 'submitting'>('idle')
  /**
   * The question the student is asked before handing in with questions still
   * unanswered. The count is kept while the dialog closes, so its title does
   * not change under it on the way out.
   */
  const [unansweredPrompt, setUnansweredPrompt] = useState({ open: false, count: 0 })
  /**
   * One submission at a time, whichever way it was asked for.
   *
   * A second click on Submit, a second tap on "Submit anyway", or one of each,
   * would otherwise be two requests. The busy button covers most of that, but
   * the dialog's button can be pressed again as the dialog closes, and a
   * render is not guaranteed to land between two clicks. The server refuses a
   * second submission of the same attempt anyway; this is so the student is
   * never shown that refusal for something they did once.
   */
  const submitBusy = useRef(false)
  /** Set by "Finish answering", so the dialog closing sends focus to the question. */
  const reviewing = useRef(false)
  /**
   * Where focus goes back to when the dialog is dismissed. Named rather than
   * left to the dialog's memory of what was focused, because the dialog's
   * close handler already decides between two places — the question, after
   * "Finish answering", or Submit — and one handler naming both is clearer
   * than one of them being implicit. (Submit keeps focus while it says
   * "Checking…": a loading button is busy, not disabled.)
   */
  const submitButtonRef = useRef<HTMLButtonElement>(null)
  const [scored, setScored] = useState<Score | null>(null)
  const [submitError, setSubmitError] = useState<string>()
  /** "Try again" on the results screen never got an answer. */
  const [retakeFailure, setRetakeFailure] = useState<unknown>()
  const [announcement, setAnnouncement] = useState('')
  const questionRef = useRef<HTMLDivElement>(null)

  const attempt = data?.attempt ?? null
  const questions = attempt?.questions ?? []

  /**
   * Whether "Try again" can do anything, from the same server-side `canStart`
   * the home screen's card uses — so the maximum-attempt, archive and
   * end-date rules are the server's, not a count redone here. Asked only once
   * there is a result to show, and from the network: this attempt has just
   * used one of the goes the cached list was counting.
   *
   * Unknown until it answers, and unknown is not "blocked": the button stays
   * usable, and `startAttempt` refuses on the server whatever this says. A
   * practice missing from the list is no longer assigned, so it cannot start.
   */
  const { data: assigned } = useQuery(MyAssignedPracticesDocument, {
    skip: attempt?.status !== 'SUBMITTED' && scored === null,
    fetchPolicy: 'network-only',
  })
  const assignedRow = assigned?.myAssignedPractices.find(
    (practice) => practice.practiceId === attempt?.practiceId,
  )
  const retakeBlocked: { reason: string | null } | null =
    assigned === undefined
      ? null
      : assignedRow === undefined
        ? { reason: null }
        : assignedRow.canStart
          ? null
          : { reason: assignedRow.startBlockedReason ?? null }
  const question: Question | undefined = questions[index]
  const isLast = index === questions.length - 1

  /**
   * Whether this practice is against the clock, and the server's own answer to
   * how long is left.
   *
   * `expiresAt` is what says the practice is timed — an untimed attempt has
   * none — and `secondsRemaining` is what the countdown runs on. The deadline
   * is never subtracted from the device's clock here, because the device's
   * clock is the one thing the student can change.
   */
  const timed = attempt !== null && attempt.expiresAt !== null
  // The tab says which practice this is, generic until the attempt arrives.
  usePageTitle(attempt?.practiceTitle ?? t('practice.title'))
  const running = timed && attempt.status === 'IN_PROGRESS'

  /**
   * Answers reach the server as the student works, rather than only when they
   * press "Save & close" (`useAttemptAutosave` says how, and why a timed
   * practice keeps saving on Next). Nothing is sent before the saved answers
   * are in place — an empty save would clear them — or once the attempt is
   * finished.
   */
  const autosave = useAttemptAutosave({
    attemptId,
    mode: timed ? 'onNext' : 'continuous',
    enabled: ready && attempt?.status === 'IN_PROGRESS' && scored === null,
    read: () => ({
      answers: toSubmittedAnswers(answersRef.current),
      currentQuestionPosition: indexRef.current,
    }),
  })
  const continuous = !timed
  const saveFailure = continuous ? autosave.state.failure : null

  // A failure is announced by its notice as it appears. Its going away is not
  // announced by anything, so the recovery is said once, in the live region —
  // which is emptied when the failure starts, so that a second recovery in one
  // sitting is still a change a screen reader will read.
  const failing = saveFailure !== null
  const wasFailing = useRef(false)
  useEffect(() => {
    if (failing) {
      wasFailing.current = true
      setAnnouncement('')
      return
    }
    if (wasFailing.current) {
      wasFailing.current = false
      setAnnouncement(t('attempt.autosaveRecovered'))
    }
  }, [failing, t])

  // When the current answer arrived, so the countdown knows to reseed. A
  // counter rather than the number itself: two reads a second apart can
  // legitimately return the same integer, and that is still fresh news.
  const [receivedAt, setReceivedAt] = useState(0)
  useEffect(() => {
    if (data?.attempt) setReceivedAt((value) => value + 1)
  }, [data])

  /**
   * Time is up, as far as this tab can tell.
   *
   * It does not submit anything: the server hands the attempt in the next time
   * anybody asks about it, so asking is the whole of what this does. What comes
   * back is a submitted attempt with a score, and the screen becomes the result
   * screen by the ordinary route.
   *
   * This is also what a second tab does when the first one submits — it finds
   * an attempt that is no longer in progress and shows the result — so the two
   * cases need no separate handling.
   */
  const onExpire = useCallback(() => {
    // Swallowed rather than surfaced: the student is mid-practice, the tab may
    // simply be offline, and there is nothing for them to do about it. The
    // deadline is enforced on the server whatever this request does, and the
    // visibility and online handlers below will try again.
    refetch()
      .then(({ data }) => {
        if (data?.attempt?.status === 'SUBMITTED') invalidateStudentActivity(client.cache)
      })
      .catch(() => undefined)
  }, [refetch, client])

  /**
   * Come back to a suspended tab, or to a connection that dropped, and ask the
   * server again rather than trusting an estimate that has been drifting.
   *
   * Only while a timed attempt is running: there is nothing to reconcile on an
   * untimed one, and refetching on every tab focus for every practice would be
   * a cost with no purpose.
   */
  useEffect(() => {
    if (!running) return

    const reconcile = () => {
      if (document.visibilityState === 'visible') onExpire()
    }
    document.addEventListener('visibilitychange', reconcile)
    window.addEventListener('online', reconcile)
    return () => {
      document.removeEventListener('visibilitychange', reconcile)
      window.removeEventListener('online', reconcile)
    }
  }, [running, onExpire])

  // Moving between questions moves focus with it, or a keyboard user is left
  // reading a new question from a control that scrolled off the screen.
  //
  // Keyed on the question as well as the index, because the first question
  // arrives after the query resolves: with `index` alone the effect fired once
  // while the ref was still null, focus stayed on the body, and Escape did
  // nothing until the user pressed Tab. And on `ready` for the same reason:
  // the question renders only once its saved answers are in place, which is a
  // render after it arrives.
  useEffect(() => {
    questionRef.current?.focus()
  }, [index, question?.id, ready])

  // Pick up where the last save left off. Runs once per attempt: a resumed
  // practice opens on the question it was saved at, with the answers that
  // were saved with it.
  //
  // From the server's answer rather than the cache's. `cache-and-network`
  // shows a cached attempt first, and one cached a while ago — before answers
  // were saved from another tab or device — would seed answers the server has
  // since replaced, and the next save would send them back over the newer
  // ones. The cached copy is used only when the network has failed, so an
  // offline student can still carry on.
  const answered = !loading || error !== undefined
  useEffect(() => {
    if (!attempt || !answered || seeded.current === attempt.id) return
    seeded.current = attempt.id

    const restored = fromSavedAnswers(attempt.savedAnswers)
    answersRef.current = restored
    setAnswers(restored)
    if (attempt.currentQuestionPosition !== null) {
      const position = Math.min(attempt.currentQuestionPosition, Math.max(questions.length - 1, 0))
      indexRef.current = position
      setIndex(position)
    }
    setReady(true)
  }, [attempt, answered, questions.length])

  // Both guarded on there being nothing to show, rather than on the query's
  // state. A timed practice refetches to reconcile its clock — on every tab
  // focus, on reconnecting, and when the countdown reaches zero — and a
  // loading screen thrown up over a half-answered question, or an error
  // screen thrown up because the wifi blinked, would be this feature taking
  // the practice away from the student to check the time.
  if (loading && !data?.attempt) return <LoadingState>{t('common.loading')}</LoadingState>
  if (error && !data?.attempt) return <ErrorState>{t('attempt.runnerLoadError')}</ErrorState>
  if (!attempt) return <ErrorState>{t('attempt.runnerNotFound')}</ErrorState>

  const finished: Score | null =
    scored ??
    (attempt.status === 'SUBMITTED' &&
    attempt.correctCount !== null &&
    attempt.questionCount !== null
      ? {
          correctCount: attempt.correctCount,
          questionCount: attempt.questionCount,
          percent: attempt.percent ?? 0,
        }
      : null)

  function answerFor(questionId: string) {
    return answers[questionId] ?? {}
  }

  function setAnswer(questionId: string, value: LocalAnswer) {
    const next = { ...answersRef.current, [questionId]: value }
    answersRef.current = next
    setAnswers(next)
    // Typing waits for a pause before it is sent; everything else goes almost
    // at once. Which it was is read off the answer, because a short-answer
    // question is the only kind that carries text.
    autosave.changed(value.textAnswer === undefined ? 'choice' : 'typing')
  }

  /** See `submitBusy`, declared with the other state. */
  async function oneSubmission(work: () => Promise<void>) {
    if (submitBusy.current) return
    submitBusy.current = true
    try {
      await work()
    } finally {
      submitBusy.current = false
    }
  }

  /**
   * Whether the answers are safely on the server, so the practice may be
   * handed in. The submission carries the answers itself, so this is not about
   * what gets scored. It is about not submitting over a save still in flight,
   * and about telling the truth: a practice whose answers could not reach the
   * server is not handed in on the assumption that they did.
   *
   * A timed practice keeps its old path — it has never waited for a save on
   * the last question, and its deadline does not wait either.
   */
  async function answersSafe() {
    if (!continuous) return true
    setSubmitPhase('saving')
    const saved = await autosave.flush()
    if (!saved) {
      setSubmitPhase('idle')
      setSubmitError(t('attempt.submitNotSaved'))
    }
    return saved
  }

  /**
   * The Submit button: hand the attempt in, or say first what is missing.
   *
   * Complete, it goes at once, with no "Are you sure?" — pressing Submit on a
   * finished practice is not a mistake worth a second step. With questions
   * unanswered it asks, because submitting uses the attempt up and cannot be
   * undone, and a student often skips a question meaning to come back to it.
   *
   * What is missing is read from the answers in the page, which include the
   * one given a moment ago, and read only after the saves are safe — so a
   * student who answers the last question and presses Submit at once is never
   * told it is unanswered, and never submits over a save still on its way.
   */
  function requestSubmit() {
    void oneSubmission(async () => {
      setSubmitError(undefined)
      if (!(await answersSafe())) return

      const unanswered = unansweredPositions(questions, answersRef.current).length
      if (unanswered > 0) {
        setSubmitPhase('idle')
        setUnansweredPrompt({ open: true, count: unanswered })
        return
      }
      await performSubmit()
    })
  }

  /** The dialog's "Submit anyway": the student has been told, and chose to. */
  function submitAnyway() {
    void oneSubmission(async () => {
      if (!(await answersSafe())) return
      await performSubmit()
    })
  }

  /**
   * "Finish answering": back to the first question without a complete
   * answer, with focus on it (`onCloseAutoFocus` below), so the student lands
   * on the work and not on the Submit button they just pressed.
   */
  function reviewUnanswered() {
    const [first] = unansweredPositions(questions, answersRef.current)
    reviewing.current = true
    if (first !== undefined) goTo(first)
  }

  async function performSubmit() {
    // Nothing more is saved while the submission is on its way: a save that
    // landed after it would be refused, and one that landed just before would
    // be moot.
    autosave.pause()
    setSubmitPhase('submitting')
    try {
      const result = await submitAttempt({
        variables: { attemptId, answers: toSubmittedAnswers(answersRef.current) },
      })
      const payload = result.data?.submitAttempt

      if (payload?.result) {
        setScored(payload.result)
        setAnnouncement(
          t('attempt.submitted', [payload.result.correctCount, payload.result.questionCount]),
        )
        return
      }
      setSubmitError(userErrorText(payload?.userErrors, t('attempt.submitFailed')))
    } catch {
      // A dropped connection used to end here without a word. The student is
      // still on the last question with every answer in place, and can submit
      // again.
      setSubmitError(t('attempt.submitFailed'))
    } finally {
      setSubmitPhase('idle')
    }
    autosave.resume()
  }

  /**
   * Save and close: make sure the latest work is on the server, then leave.
   *
   * Answers are saved as the student goes, so this is usually no more than a
   * last word on which question they are on. It still waits for the server,
   * and leaves only once the server has said yes: a student who presses it
   * offline stays in the practice with everything they did, and is told why,
   * rather than landing on the home screen with work that never arrived.
   *
   * The attempt stays in progress and nothing is scored: saved answers live in
   * a table that holds no correctness.
   */
  async function onSaveAndClose() {
    setSaveError(undefined)
    setClosing(true)
    const saved = await autosave.saveBeforeLeaving()
    if (saved) {
      invalidateStudentActivity(client.cache)
      await navigate({ to: '/$language/learn', params: { language } })
      // Said on the home screen, where the student now is: the runner and its
      // button are gone. Only here — autosave stays quiet when it works.
      toast.show(t('attempt.savedAndClosed'))
      return
    }
    setClosing(false)
    // An untimed practice already says so, in the notice above the buttons. A
    // timed one keeps its saving quiet, so the refusal is said here instead.
    if (timed) setSaveError(autosave.getState().failure?.message ?? t('attempt.saveFailed'))
  }

  async function onRetake() {
    setSubmitError(undefined)
    let result
    try {
      result = await startAttempt({ variables: { practiceId: attempt?.practiceId ?? '' } })
    } catch (error) {
      // Safe to press again: starting resumes the attempt a first start made.
      setRetakeFailure(error)
      return
    }
    setRetakeFailure(undefined)
    const next = result.data?.startAttempt.attempt
    if (next) {
      await navigate({
        to: '/$language/learn/attempt/$attemptId',
        params: { language, attemptId: next.id },
      })
      return
    }
    setSubmitError(userErrorText(result.data?.startAttempt.userErrors, t('learn.startFailed')))
  }

  function goTo(position: number) {
    indexRef.current = position
    setIndex(position)
  }

  function advance() {
    // Moving on never hands the practice in: the last question has nowhere to
    // move on to, and submitting takes pressing Submit itself.
    if (isLast) return
    goTo(indexRef.current + 1)
    // Moving on is saved straight away, on either kind of practice: the answers
    // so far and the question the student is now on. On a timed practice it is
    // the only save there is, because the deadline hands in what was saved — a
    // student who worked through nine questions and ran out of time on the
    // tenth should be marked on the nine. Its failures stay quiet, as they
    // always have: the next question saves again.
    autosave.changed('move')
    void autosave.flush()
  }

  function goBack() {
    goTo(indexRef.current - 1)
    // Going back is not a reason to save by itself, but on an untimed practice
    // an answer still waiting for its pause is sent now rather than left behind.
    if (continuous && !autosave.isSaved()) void autosave.flush()
  }

  /**
   * The runner's one key of its own. Everything else is a control's: letters
   * belong to the question (`handleOptionLetterKey`), arrows to the radio
   * group, Enter and Space to whatever button or dropdown has focus.
   */
  function onKeyDown(event: KeyboardEvent<HTMLElement>) {
    // A key something else has already handled is not the runner's. React
    // bubbles a dialog's keys up to here through its portal, and a dialog that
    // closed on Escape would otherwise open "Stop for now?" behind itself.
    if (event.defaultPrevented) return

    if (event.key === 'Escape') {
      event.preventDefault()
      setExiting(true)
    }
  }

  /**
   * Enter in a written answer: "done with this one", as it is in any single
   * field, so it moves on. It is the only place Enter moves on. On an option
   * it means what it means to a radio, which is nothing; on a button or a
   * dropdown it is that control's. And on the last question it does nothing
   * here either: handing a practice in uses the attempt up and cannot be
   * undone, so it takes pressing Submit, never a key pressed while answering.
   */
  function onAnswerFieldKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    // Enter that confirms an accented letter being composed is not "done".
    if (event.key !== 'Enter' || event.nativeEvent.isComposing) return
    event.preventDefault()
    advance()
  }

  // Held until the saved answers are in place, so that nothing can be answered
  // — and saved — over answers that are about to be restored.
  if (!finished && !ready) return <LoadingState>{t('common.loading')}</LoadingState>

  if (finished) {
    const forThisPractice = (history?.myAttempts ?? []).filter(
      (row) => row.practiceId === attempt.practiceId && row.status === 'SUBMITTED',
    )
    const best = forThisPractice.reduce<number | null>(
      (highest, row) =>
        row.percent === null || (highest !== null && row.percent <= highest)
          ? highest
          : row.percent,
      null,
    )

    /**
     * Which go this was, and which go each row in the history was.
     *
     * `myAttempts` comes back ordered by `startedAt` descending, so the list
     * is newest first and the attempt just submitted is at the front. Its
     * number is therefore the length of the list, and a row's number counts
     * back from there — not `position + 1`, which labelled the newest attempt
     * "Attempt 1" and the student's very first go as their highest-numbered.
     */
    const attemptNumber = forThisPractice.length
    const numberFor = (position: number) => attemptNumber - position

    return (
      <main className="mx-auto flex min-h-dvh w-full max-w-container-max flex-col justify-center gap-8 px-4 py-6 md:px-6">
        <h1 className="text-h1 font-bold text-text">{attempt.practiceTitle}</h1>

        {/* The one case a submitted attempt needs a sentence: the student did
            not press submit, the deadline did. `scored` is set only by this
            tab's own submission, so its absence on a timed attempt is what
            says the server handed it in. */}
        {timed && scored === null ? (
          <Notice title={t('attempt.timer.expired')}>{t('attempt.expiredNotice')}</Notice>
        ) : null}

        {/* "Attempt 3", not "Attempt 3 of 3". The attempt just submitted is
            the newest, so its number is the count of submitted attempts —
            which the old wording printed twice and turned into a tautology.
            The handoff's own format is the bare ordinal ("Attempt #2"), and
            the list below gives the total anyway. */}
        <ScoreIndicator
          correctCount={finished.correctCount}
          questionCount={finished.questionCount}
          detail={t('attempt.attemptNumber', [attemptNumber])}
        />

        <div className="flex flex-wrap items-center gap-4">
          <Button
            size="lg"
            onClick={() => void onRetake()}
            loading={starting}
            loadingText={t('common.opening')}
            disabled={retakeBlocked !== null}
            {...(retakeBlocked === null ? {} : { 'aria-describedby': 'retake-blocked' })}
          >
            {t('attempt.tryAgain')}
          </Button>
          <Link
            to="/$language/learn"
            params={{ language }}
            className={buttonClasses({ variant: 'secondary', size: 'lg' })}
          >
            {t('attempt.backToPractices')}
          </Link>
        </div>

        {/* A disabled button says nothing on its own, so the reason is in
            words beside it — the same words the home screen's card uses. */}
        {retakeBlocked === null ? null : (
          <p id="retake-blocked" className="text-body-sm font-medium text-text-secondary">
            {t(startBlockedKey(retakeBlocked.reason))}
          </p>
        )}

        {submitError ? <FieldMessage tone="error">{submitError}</FieldMessage> : null}
        {retakeFailure === undefined ? null : (
          <FailureNotice
            title={t('learn.startFailed')}
            error={retakeFailure}
            retrying={starting}
            onRetry={() => void onRetake()}
          />
        )}

        {/* The question-by-question review, when the practice allows it. The
            server decides: it opens only once no further attempt is possible,
            and refuses otherwise, so there is no rule duplicated here. */}
        <CompletionReview attemptId={attemptId} />

        <Section id="history" title={t('attempt.yourAttempts')}>
          <AttemptSummary
            emptyMessage={t('attempt.firstAttempt')}
            attempts={forThisPractice.map((row, position) => ({
              id: row.id,
              label: t('attempt.attemptNumber', [numberFor(position)]),
              score: t('common.ofTotal', [row.correctCount ?? 0, row.questionCount ?? 0]),
              best: row.percent !== null && best !== null && row.percent === best,
            }))}
          />
        </Section>

        <LiveRegion message={announcement} />
      </main>
    )
  }

  if (!question) {
    return (
      <main>
        <ErrorState>{t('attempt.noQuestions')}</ErrorState>
      </main>
    )
  }

  const current = answerFor(question.id)

  return (
    <main
      onKeyDown={onKeyDown}
      // The same container the shell gives every other page: `container-max`
      // with `px-4 md:px-6`. The runner sits outside the shell — a practice in
      // progress holds the question and nothing else — but sitting outside the
      // shell is a reason to have no sidebar, not a reason to be narrower than
      // every other page in the app. No `pb-24`: that is the shell's room for
      // the mobile bottom bar, and there is no bottom bar here.
      className="mx-auto flex min-h-dvh w-full max-w-container-max flex-col gap-8 px-4 py-6 md:px-6"
    >
      {/* The page's heading is the practice, and "Question 4 of 18" — the
          progress label below — is the section under it. The practice title
          is not drawn: the runner shows the question and its progress and
          nothing else, on purpose. A screen-reader user still gets both from
          heading navigation — which practice, which question. */}
      <h1 className="sr-only">{attempt.practiceTitle}</h1>

      {/* Sticky, so the clock does not scroll away on a long question — the
          one thing a student in an exam must be able to see at any moment. The
          progress bar comes with it rather than being left behind: they are
          one header, and splitting them would leave a bar floating under a
          detached pill. */}
      <div
        ref={topInset}
        className={cn(
          'flex flex-wrap items-center justify-between gap-3 bg-background',
          running && 'sticky top-0 z-10 -mx-4 px-4 py-3 md:-mx-6 md:px-6',
        )}
      >
        <ProgressLinear
          className="min-w-48 flex-1"
          value={((index + 1) / questions.length) * 100}
          label={t('attempt.questionProgress', [index + 1, questions.length])}
          // "Question 4 of 18" is the section the question sits in, under the
          // practice's own h1: the visible label is that heading, rather than a
          // hidden copy of it.
          labelLevel={2}
        />
        {running && attempt.secondsRemaining !== null ? (
          <AttemptTimer
            secondsRemaining={attempt.secondsRemaining}
            receivedAt={receivedAt}
            onExpire={onExpire}
          />
        ) : null}
      </div>

      {/* The question, and the boundary of its keys. Focus comes here when a
          question appears, so the prompt is read before anything is chosen —
          and a letter key works from here on, so choosing does not first take
          tabbing into the options. */}
      <div
        ref={questionRef}
        tabIndex={-1}
        onKeyDown={handleOptionLetterKey}
        className="flex flex-1 flex-col gap-6 outline-none"
      >
        {/* Switched on the question's type rather than on whether it has
            options, because an ordering question has options too and would
            otherwise arrive as a list of radio buttons. */}
        {question.type === 'MATCHING' ? (
          <MatchingBoard
            legend={question.prompt}
            leftItems={question.leftItems}
            rightItems={question.rightItems}
            value={current.matches ?? {}}
            onChange={(next) => setAnswer(question.id, { matches: next })}
            idPrefix={`question-${question.id}`}
          />
        ) : question.type === 'ASSOCIATION' ? (
          <AssociationOrdering
            legend={question.prompt}
            options={question.options}
            value={current.submittedOrder ?? []}
            onChange={(next) => setAnswer(question.id, { submittedOrder: next })}
            idPrefix={`question-${question.id}`}
          />
        ) : question.options.length > 0 ? (
          <QuestionOptionGroup legend={question.prompt}>
            {question.options.map((option) => (
              <QuestionOption
                key={option.id}
                name={`question-${question.id}`}
                value={option.id}
                letter={option.label}
                checked={current.selectedOptionId === option.id}
                onSelect={(value) => setAnswer(question.id, { selectedOptionId: value })}
              >
                <RichText inline>{option.text}</RichText>
              </QuestionOption>
            ))}
          </QuestionOptionGroup>
        ) : (
          /* Capped at the reading measure, like every other question type.
             The three that have a fieldset carry it on their own legend; a
             short answer has no fieldset, so the cap sits here — on the
             column, as `StudentPreview` does it, so the field is held to the
             same width as the prompt above it. Without this the one question
             type with no options was also the one that ran the full
             `container-max` width of the main below. */
          <div className="flex max-w-measure-max flex-col gap-6">
            <p className="text-question text-text text-pretty">
              <RichText inline>{question.prompt}</RichText>
            </p>
            <TextInput
              label={t('attempt.yourAnswer')}
              value={current.textAnswer ?? ''}
              onChange={(event) => setAnswer(question.id, { textAnswer: event.target.value })}
              onKeyDown={onAnswerFieldKeyDown}
            />
          </div>
        )}
      </div>

      {submitError ? <FieldMessage tone="error">{submitError}</FieldMessage> : null}
      {saveError ? <FieldMessage tone="error">{saveError}</FieldMessage> : null}

      {/* Sticky where the screen has room for it, and back in the flow where
          it does not. On a 320x568 phone the three rows of buttons were a
          third of the screen, and with the save warning in them two thirds,
          leaving a line of the question visible — and at 200% zoom on a
          laptop it is the same screen. Below 40rem of height the buttons
          follow the question instead, reached by scrolling, which a phone
          does anyway. The height it covers is published so that focus
          scrolls clear of it, and it keeps off a phone's home indicator. */}
      <div
        ref={bottomInset}
        className={cn(
          'flex flex-col gap-3 bg-background pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]',
          'sticky bottom-0 [@media(max-height:40rem)]:static',
        )}
      >
        {/* An answer that has not reached the server, said where the student is
            working, and kept on screen until a save succeeds — sticky with the
            buttons, so a long question cannot scroll it out of sight. A warning
            rather than an error: nothing is lost while the page is open, and
            the student can carry on. Nothing is shown while saves succeed. */}
        {saveFailure ? (
          <Notice
            tone="warning"
            title={t('attempt.autosaveFailed')}
            actions={
              <Button variant="secondary" size="lg" onClick={autosave.retry}>
                {t('common.tryAgain')}
              </Button>
            }
          >
            {saveFailure.retryable
              ? t('attempt.autosaveRetrying')
              : (saveFailure.message ?? t('attempt.saveFailed'))}
          </Notice>
        ) : null}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button variant="secondary" size="lg" disabled={index === 0} onClick={goBack}>
            {t('attempt.back')}
          </Button>

          <div className="flex flex-wrap items-center gap-3">
            {/* Ghost, because stopping is not the thing this screen is for. It is
                here rather than hidden behind Escape so a student can find it. */}
            {/* On a timed practice this opens the dialog rather than leaving
                straight away, so "the clock keeps running" is said before the
                student acts on it rather than after. */}
            <Button
              variant="ghost"
              size="lg"
              onClick={() => (running ? setExiting(true) : void onSaveAndClose())}
              loading={closing}
              loadingText={t('common.saving')}
            >
              {running ? t('attempt.saveAndLeave') : t('attempt.saveAndClose')}
            </Button>

            <Button
              ref={submitButtonRef}
              size="lg"
              onClick={isLast ? requestSubmit : advance}
              loading={submitPhase !== 'idle'}
              loadingText={t('attempt.checking')}
            >
              {isLast ? t('attempt.submit') : t('attempt.next')}
            </Button>
          </div>
        </div>
      </div>

      {/* Escape offers to save and leave rather than warning about losing work.
          On an untimed practice the answers are already saving as the student
          goes, and "Save & close" makes sure the last of them has landed before
          it leaves — which makes it the useful answer to "let me out". Saving
          loses nothing, so nothing here is red: `default`, with "Save & close"
          as the primary action and "Keep going" first, where focus starts. */}
      <ConfirmationDialog
        open={exiting}
        onOpenChange={setExiting}
        intent="default"
        // Two readings of the same action, because leaving means two different
        // things. On an untimed practice the work waits. On a timed one the
        // clock does not stop, and a student who is not told that will find
        // out by losing the practice.
        title={running ? t('attempt.stopTimedTitle') : t('attempt.stopTitle')}
        description={running ? t('attempt.stopTimedConsequence') : t('attempt.stopConsequence')}
        confirmLabel={running ? t('attempt.saveAndLeave') : t('attempt.saveAndClose')}
        cancelLabel={t('attempt.keepGoing')}
        onConfirm={() => {
          void onSaveAndClose()
        }}
      />

      {/* Leaving while an answer is still not on the server — by the browser's
          back button, say, with the connection down. Leaving normally asks
          nothing: the guard waits for an outstanding save first and only
          stops the student when the answer still could not be saved. Losing
          it is a real loss, so this one is destructive, and staying is the
          default. */}
      <ConfirmationDialog
        open={autosave.leaving !== null}
        intent="destructive"
        onOpenChange={(open) => {
          // The confirm button confirms and then closes the dialog, in one
          // click. Staying is decided a tick later all the same, so that
          // "Leave anyway" is always heard first; a navigation can only be
          // decided once, so the later call is then a no-op.
          const leaving = autosave.leaving
          if (!open) queueMicrotask(() => leaving?.stay())
        }}
        title={t('attempt.leaveUnsavedTitle')}
        description={t('attempt.leaveUnsavedConsequence')}
        confirmLabel={t('attempt.leaveAnyway')}
        cancelLabel={t('attempt.keepGoing')}
        onConfirm={() => autosave.leaving?.proceed()}
      />

      {/* Handing in with questions still unanswered. Asked, not refused: the
          student may mean to. Nothing is destroyed, so nothing is red — going
          back to finish is the recommendation and takes the primary button and
          the first focus, and "Submit anyway" is plain. The count is the
          title, so it is the first thing a screen reader says. Escape and a
          click outside close it and leave the student where they were;
          "Finish answering" takes them to the first question still missing. */}
      <ConfirmationDialog
        open={unansweredPrompt.open}
        onOpenChange={(open) => {
          if (!open) setUnansweredPrompt((prompt) => ({ ...prompt, open: false }))
        }}
        title={
          unansweredPrompt.count === 1
            ? t('attempt.unansweredOne')
            : t('attempt.unansweredOther', [unansweredPrompt.count])
        }
        description={t('attempt.unansweredConsequence')}
        // Not a deletion, so not red; not the recommendation either, so the
        // way back takes the primary button and the first focus.
        intent="caution"
        confirmLabel={t('attempt.submitAnyway')}
        cancelLabel={t('attempt.finishAnswering')}
        onCancel={reviewUnanswered}
        onConfirm={submitAnyway}
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          if (reviewing.current) {
            reviewing.current = false
            questionRef.current?.focus()
            return
          }
          submitButtonRef.current?.focus()
        }}
      />

      <LiveRegion message={announcement} />
    </main>
  )
}
